import type { FromBasePlayerInput, FromBaseRow as Row } from './from-base-context';
import { fromBaseRecord, FromBaseInputMissing } from './from-base-context';
import { readItemField } from './item-field-reader';
import { phpJsonInt, phpJsonString, phpJsonTruthy } from './php-json-values';
import { normalizeDancePlaylist } from '../../encounter-creation-engine/ft3d-emote-clips';

/** Raw saved rows become the native class/list map before the PC finalizer. */
export function constructPlayerPreparedMapFromBase(base: FromBasePlayerInput): Row {
    const saved = readItemField({ rows: [base.encounter_creatures_spells_prepared ?? []] }, 'rows');
    if (!Array.isArray(saved)) throw new FromBaseInputMissing('saved-prepared-spell-rows', base.entity_id);
    const map: Record<string, Record<string, unknown[]>> = {};
    for (const row of saved) {
        const classId = readItemField(row, 'class_id'), list = readItemField(row, 'spell_list'), spell = readItemField(row, 'spell_id');
        if (classId !== null && classId !== '' && list !== null && list !== '' && spell !== null && spell !== '')
            ((map[phpJsonString(classId)] ??= {})[phpJsonString(list)] ??= []).push(spell);
    }
    return Object.keys(map).length ? { encounter_creatures_spells_prepared: map } : {};
}

/** Saved action-bar rows use the native bar/slot presentation contract. */
export function constructPlayerActionBarFromBase(bar: Row, slots: readonly Row[]): Row {
    const id = readItemField(bar, 'bar_id');
    const row: Row = { bar_id: id, bar_index: phpJsonInt(readItemField(bar, 'bar_index')), size: readItemField(bar, 'size') ?? 'm',
        slot_count: phpJsonInt(readItemField(bar, 'slot_count') ?? 12), position: readItemField(bar, 'position') ?? 'bottom', slots: [] };
    for (const key of ['label', 'theme']) { const value = readItemField(bar, key); if (value != null && value !== '') row[key] = value; }
    for (const saved of slots) {
        if (readItemField(saved, 'bar_id') !== id) continue;
        const ability = readItemField(saved, 'ability_id'), hot = readItemField(saved, 'hot_key');
        if (!phpJsonTruthy(ability) && (hot === null || hot === '')) continue;
        const slot: Row = { slot_index: phpJsonInt(readItemField(saved, 'slot_index')) };
        if (phpJsonTruthy(ability)) {
            slot.ability_id = ability;
            for (const key of ['source_id', 'slot_type']) { const value = readItemField(saved, key); if (phpJsonTruthy(value)) slot[key] = value; }
        }
        const slotId = readItemField(saved, 'slot_id'); if (phpJsonTruthy(slotId)) slot.slot_id = slotId;
        if (hot !== null) slot.hot_key = phpJsonString(hot);
        (row.slots as Row[]).push(slot);
    }
    return row;
}

export function constructPlayerActionBarsFromBase(base: FromBasePlayerInput): Row {
    const raw = base as unknown as Row;
    for (const key of ['action_bars', 'action_bar_slots', 'action_bar_dividers'])
        if (!Array.isArray(raw[key])) throw new FromBaseInputMissing('player-display-rows', key);
    const bars: Row[] = [];
    for (const bar of raw.action_bars as Row[]) {
        const id = readItemField(bar, 'bar_id'); if (!phpJsonTruthy(id)) continue;
        bars.push(constructPlayerActionBarFromBase(bar, raw.action_bar_slots as Row[]));
    }
    const dividers = (raw.action_bar_dividers as Row[]).filter(fromBaseRecord).map(row => phpJsonInt(readItemField(row, 'position')));
    const settings: Row = {};
    const savedSettings = readItemField({ rows: [raw.action_bar_settings ?? []] }, 'rows');
    if (!Array.isArray(savedSettings)) throw new FromBaseInputMissing('player-display-rows', 'action_bar_settings');
    for (const row of savedSettings) if (readItemField(row, 'action_bar_setting_key') === 'dance_playlist') {
        let decoded: unknown = null;
        try { decoded = JSON.parse(phpJsonString(readItemField(row, 'action_bar_setting_value'))); } catch { /* Native json_decode returns null. */ }
        settings.dance_playlist = normalizeDancePlaylist(Array.isArray(decoded) ? decoded.filter(key => typeof key === 'string') : decoded);
    }
    return { ...(bars.length ? { action_bars: bars } : {}), ...(bars.length || dividers.length ? { action_bar_dividers: dividers } : {}),
        ...(Object.keys(settings).length ? { action_bar_settings: settings } : {}) };
}
